import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
  RefObject,
} from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import useDebounce from 'redux/utils/debounceHook';
import ReactQuill from 'react-quill-new';

import { removeHtmlTags } from '@act/shared/utilities';
import { Typography } from '@mui/material';
import SaveStatusIndicator from 'toolkit/inputs/SaveStatusIndicator';
import { BaseControlledInputProps, FlexContainer } from '@act/storybook';
import { useTracking } from '../tracking';

interface RTEContainerProps {
  children: React.ReactNode;
  $height?: string | number;
}

/**
 * Layout shell for the Quill editor with optional full-height mode.
 * @param props - Container children and optional height.
 * @returns Rich text editor container.
 */
export const RTEContainer = ({ children, $height }: RTEContainerProps) => {
  const useFillHeight =
    $height === '100%' ||
    (typeof $height === 'string' && $height.endsWith('%'));

  const explicitHeight =
    $height != null && !useFillHeight
      ? typeof $height === 'number'
        ? `${$height}px`
        : $height
      : undefined;

  const containerClass = [
    'rich-text-editor-container',
    useFillHeight ? 'rich-text-editor-container--fill' : '',
    explicitHeight ? 'rich-text-editor-container--sized' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const resolvedHeight = useFillHeight
    ? '100%'
    : explicitHeight ?? 'auto';

  return (
    <div className={containerClass}>
      <FlexContainer
        direction="column"
        width="100%"
        height={resolvedHeight}
        overflowAllVisible
      >
        {children}
      </FlexContainer>
    </div>
  );
};

export const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'link',
];

export const getModules = (readOnly: boolean) => ({
  toolbar: readOnly
    ? false
    : [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
      ],
});

export interface RichTextEditorProps {
  /**
   * `Control<T>` is invariant in T; use `unknown` here so any `Control<YourForm>` is accepted at call sites.
   */
  control: unknown;
  defaultValue?: string;
  name: string;
  readOnly?: boolean;
  showCharacterCount?: boolean;
  autoSaveFn?: (debouncedValue: string) => void | Promise<void>;
  size?: 'small' | 'large';
  required?: boolean;
  placeholderText?: string;
  height?: string | number;
  trackingName?: string;
  showSaveIndicator?: boolean;
}

export interface RichTextEditorRef {
  cancelPendingAutoSave: () => void;
  markAsSaved: (value: string) => void;
  getCurrentValue: () => string;
}

const AUTOSAVE_UPDATE_THRESHOLD = 5;
export type ControlledRichTextProps<
  FORM_TYPE extends FieldValues,
  FIELD_NAME extends Path<FORM_TYPE>,
> = Omit<RichTextEditorProps, 'control' | 'name'> &
  BaseControlledInputProps<FORM_TYPE, FIELD_NAME> & {
    ref?: RefObject<RichTextEditorRef>;
  };

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    {
      control,
      defaultValue,
      name,
      readOnly,
      showCharacterCount,
      autoSaveFn = null,
      required = false,
      placeholderText = 'Add notes here...',
      height,
      trackingName,
      showSaveIndicator = true,
    },
    ref
  ) => {
    const [status, setStatus] = useState<'IDLE' | 'SAVING' | 'SAVED'>('IDLE');
    const [value, setValue] = useState<string>(defaultValue || '');
    const [isAutoSaving, setIsAutoSaving] = useState(false);

    const lastSavedValueRef = useRef(defaultValue || '');
    const initialValueRef = useRef(defaultValue || '');
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const debounceResult = useDebounce<string>(value, 1500);
    const debouncedValue = debounceResult[0];
    const cancelDebounce = debounceResult[1];
    const quillModules = useMemo(() => getModules(!!readOnly), [readOnly]);

    const { generateTrackingProps } = useTracking();

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (statusTimerRef.current) {
          clearTimeout(statusTimerRef.current);
        }
      };
    }, []);

    // Expose methods to parent components
    useImperativeHandle(
      ref,
      () => ({
        cancelPendingAutoSave: () => {
          cancelDebounce();
          setIsAutoSaving(false);
          setStatus('IDLE');
          if (statusTimerRef.current) {
            clearTimeout(statusTimerRef.current);
            statusTimerRef.current = null;
          }
        },
        markAsSaved: (value: string) => {
          lastSavedValueRef.current = value;
        },
        getCurrentValue: () => {
          return value;
        },
      }),
      [cancelDebounce, value]
    );

    // Simple auto-save logic with threshold
    const shouldAutoSave = useMemo(() => {
      if (!autoSaveFn || isAutoSaving) {
        return false;
      }

      // Don't save empty content
      if (
        !debouncedValue ||
        debouncedValue === '<p><br></p>' ||
        debouncedValue.trim() === ''
      ) {
        return false;
      }

      // Only save if content has actually changed significantly
      const hasChanged = debouncedValue !== lastSavedValueRef.current;
      const hasSignificantChange =
        Math.abs(debouncedValue.length - initialValueRef.current.length) >=
        AUTOSAVE_UPDATE_THRESHOLD;

      return hasChanged && hasSignificantChange;
    }, [debouncedValue, autoSaveFn, isAutoSaving]);

    const handleAutoSave = useCallback(async () => {
      if (!shouldAutoSave) return;

      try {
        setIsAutoSaving(true);
        if (showSaveIndicator) {
          setStatus('SAVING');
        }

        await autoSaveFn(debouncedValue);
        lastSavedValueRef.current = debouncedValue;

        if (showSaveIndicator) {
          setStatus('SAVED');
          // Clear any existing timer
          if (statusTimerRef.current) {
            clearTimeout(statusTimerRef.current);
          }
          // Set new timer
          statusTimerRef.current = setTimeout(() => {
            setStatus('IDLE');
            statusTimerRef.current = null;
          }, 1750);
        }
      } catch (error) {
        console.error('Auto-save failed:', error);
        if (showSaveIndicator) {
          setStatus('IDLE');
        }
      } finally {
        setIsAutoSaving(false);
      }
    }, [shouldAutoSave, debouncedValue, autoSaveFn, showSaveIndicator]);

    // Update value when defaultValue changes
    useEffect(() => {
      if (defaultValue !== undefined) {
        setValue(defaultValue);
        lastSavedValueRef.current = defaultValue;
        initialValueRef.current = defaultValue;
      }
    }, [defaultValue]);

    // Trigger auto-save when conditions are met
    useEffect(() => {
      if (shouldAutoSave) {
        void handleAutoSave();
      }
    }, [shouldAutoSave, handleAutoSave]);

    const characterCount = useMemo(() => {
      if (!showCharacterCount) return 0;
      return removeHtmlTags(value || defaultValue).length;
    }, [value, defaultValue, showCharacterCount]);

    return (
      <>
        <RTEContainer $height={height}>
          {autoSaveFn && showSaveIndicator && (
            <div className="rich-text-editor-status">
              <SaveStatusIndicator status={status} />
            </div>
          )}

          <Controller
            name={name}
            defaultValue={defaultValue}
            control={control as Control<FieldValues>}
            rules={{ required }}
            render={({
              field: { onChange, value: formValue },
              fieldState: { error },
            }) => {
              useEffect(() => {
                if (formValue !== value) {
                  setValue(formValue || '');
                  // Update refs when form value changes externally
                  lastSavedValueRef.current = formValue || '';
                  initialValueRef.current = formValue || '';
                }
              }, [formValue, value]);

              const handleChange = useCallback(
                (content: string) => {
                  setValue(content);
                  onChange(content);
                },
                [onChange]
              );

              return (
                <>
                  <div
                    {...(generateTrackingProps && trackingName
                      ? generateTrackingProps(trackingName)
                      : {})}
                  >
                    <ReactQuill
                      value={value}
                      onChange={handleChange}
                      readOnly={readOnly}
                      modules={quillModules}
                      formats={formats}
                      placeholder={placeholderText}
                    />
                  </div>
                  {error && (
                    <div className="rich-text-editor-error-text">
                      <Typography variant="bodySmall" color="error">
                        {error.message}
                      </Typography>
                    </div>
                  )}
                </>
              );
            }}
          />
        </RTEContainer>
        {showCharacterCount && (
          <div className="rich-text-editor-helper-text">
            <Typography variant="bodySmall">
              {characterCount} character(s)
            </Typography>
          </div>
        )}
      </>
    );
  }
);

// set the display name of the component
RichTextEditor.displayName = 'RichTextEditor';
export default RichTextEditor;
